import { InteractionEvent, Loader, Sprite } from 'pixi.js';
import { Game, Scene, Store, TextureFactory } from '../core';
import { DrawingPatternData, IEventEmitter, Updatable } from '../interface';
import { Apple, Canvas, Cucumber, Eggplant, Player, Pumpkin, TutorialCucumber, Watermelon } from '../class/drawing';
import { VanishText, DrawingGameUI } from '../ui';
import SecondsCutScene from './SecondsCutScene';
import MainScene from './MainScene';
import { secondsFormatting, secondsToScore } from '../util';
import { sound } from '@pixi/sound';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';
import { loadGoogleAdvertisement, showGoogleAdvertisement } from '../util/AD';

type MonsterClass =
  | typeof Pumpkin
  | typeof Watermelon
  | typeof Apple
  | typeof Eggplant
  | typeof Cucumber
  | typeof TutorialCucumber;

class DrawingGameScene extends Scene {
  private canvas: Canvas;
  private ui: DrawingGameUI;
  private player: Player;
  private objects: Array<Updatable & IEventEmitter>;
  private isBgmPlaying: boolean = false;

  private level: number = 0;
  private monsterQueue: Array<string> = [];
  private spawnDt: number = 0;
  private spawnMaxDt: number = 120;
  private monsterLength: number = 0;
  private monsterMaxLength: number = 6;

  constructor(game: Game) {
    super(game);

    this.camera.setOffset({ x: 0, y: 0 });
    this.camera.setTargetPosition({ x: 540, y: 960 }, true);

    const bg = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_drawing.png'));
    this.stage.addChild(bg);

    this.objects = [];
    this.player = new Player();
    this.player.position.x = 540;
    this.player.position.y = 940;
    this.player.on('DIE', (): void => {
      this.gameOver();
    });

    this.objects.push(this.player);
    this.stage.addChild(this.player.container);

    this.canvas = new Canvas();
    this.canvas.on('drawing-pattern-found', (result: { similarity: number; patternData?: DrawingPatternData }) => {
      this.player.trigger(`MAGIC_${result.patternData?.pattern}`);
      for (const object of this.objects) {
        object.emit('symbol', result.patternData?.pattern);
      }
    });
    this.uiContainer.addChild(this.canvas);

    this.ui = new DrawingGameUI(this);
    this.uiContainer.addChild(this.ui);
    this.ui.on('home', () => {
      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(MainScene);
      logging(
        LOG_EVENT.GO_TO_HOME,
        LOG_SCENE.DRAWING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.ui.on('pause', () => {
      this.game.stop();
    });
    this.ui.on('start', () => {
      this.game.start();
    });

    this.ui.on('ad', async () => {
      try {
        const isLoaded: boolean = await loadGoogleAdvertisement();
        if (isLoaded) {
          await showGoogleAdvertisement();
          logging(
            LOG_EVENT.AD,
            LOG_SCENE.DRAWING_GAME_SCENE,
            Store.instance.data.playername,
            window.navigator.userAgent
          );
        }
      } catch (e) {
        console.log(e);
      }

      if (Store.instance.state.hp < 3) {
        Store.instance.state.hp = 3;
      }

      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(DrawingGameScene);
      logging(
        LOG_EVENT.REPLAY,
        LOG_SCENE.DRAWING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });

    this.on('shared', () => {
      this.ui.notificationNinimoDT = 120;
      logging(
        LOG_EVENT.COPY_SHARE,
        LOG_SCENE.DRAWING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });

    this.generateMonster('TutorialCucumber', 0);

    logging(
      LOG_EVENT.GAME_START,
      LOG_SCENE.DRAWING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
  }

  public generateMonster(monsterClass: string, _radian?: number): void {
    const monsterClasses: Record<string, MonsterClass> = {
      Pumpkin: Pumpkin,
      Watermelon: Watermelon,
      Eggplant: Eggplant,
      Apple: Apple,
      Cucumber: Cucumber,
      TutorialCucumber: TutorialCucumber,
    };
    const monster = new monsterClasses[monsterClass]();

    if (monsterClass === 'Apple') {
      const radian =
        Math.random() < 0.5
          ? (Math.PI / 180) * 55 - ((Math.random() * Math.PI) / 180) * 10 - (Math.random() < 0.5 ? Math.PI : 0)
          : -(Math.PI / 180) * 55 + ((Math.random() * Math.PI) / 180) * 10 - (Math.random() < 0.5 ? Math.PI : 0);
      monster.position.x = this.player.position.x + Math.cos(radian) * 350;
      monster.position.y = this.player.position.y + Math.sin(radian) * 350;
    } else {
      const radian =
        _radian !== undefined
          ? _radian
          : (Math.PI / 180) * 35 - ((Math.random() * Math.PI) / 180) * 70 - (Math.random() < 0.5 ? Math.PI : 0);
      monster.position.x = this.player.position.x + Math.cos(radian) * 760;
      monster.position.y = this.player.position.y + Math.sin(radian) * 760;
    }
    monster.setTargetObject(this.player);

    this.monsterLength++;

    monster.on('damaged', (damage: number) => {
      const scoreText: VanishText = new VanishText(`HP -${damage}`, { fill: 0xff5555 });
      scoreText.x = monster.position.x;
      scoreText.y = monster.position.y - 90;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    });

    monster.on('destroy', () => {
      if (--this.monsterLength === 0 && this.monsterQueue.length === 0) {
        this.nextLevel();
      }

      const scoreText: VanishText = new VanishText(`+${monster.score}`, { fill: 0xefa500 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 80;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
      this.player.score += monster.score;

      monster.destroy();
      monster.container.removeChild(monster.container.parent);
      this.objects.splice(this.objects.indexOf(monster), 1);
    });

    monster.on('attack', (damage: number) => {
      const scoreText: VanishText = new VanishText(`HP -${damage}`, { fill: 0xff5555 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 80;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
      this.player.hit(damage);
      sound.play('/audio/heartlose.mp3', { loop: false, volume: Store.instance.state.volume as number });
    });

    this.objects.push(monster);
    this.stage.addChild(monster.container);
  }

  private nextLevel(): void {
    this.level++;
    switch (this.level) {
      case 1:
        this.ui.showTitle('Start!');
        this.spawnDt = 0;
        this.spawnMaxDt = 110;
        this.monsterMaxLength = 1;
        this.generateMonsterQueue(2);
        this.isRunning = true;
        this.isStarted = true;
        break;
      case 2:
        this.spawnDt = 0;
        this.spawnMaxDt = 100;
        this.monsterMaxLength = 2;
        this.generateMonsterQueue(2);
        break;
      case 3:
        this.spawnDt = 0;
        this.spawnMaxDt = 90;
        this.monsterMaxLength = 3;
        this.generateMonsterQueue(5);
        break;
      case 4:
        this.spawnDt = 0;
        this.spawnMaxDt = 90;
        this.monsterMaxLength = 4;
        this.generateMonsterQueue(10);
        break;
      case 5:
        this.spawnDt = 0;
        this.spawnMaxDt = 80;
        this.monsterMaxLength = 5;
        this.generateMonsterQueue(25);
        break;
      default:
        this.gameClear();
        break;
    }
  }

  public get randomMonsterClass(): string {
    const monsterClasses = [
      { monsterClass: 'Pumpkin', probability: 0.05 },
      { monsterClass: 'Watermelon', probability: 0.1 },
      { monsterClass: 'Eggplant', probability: 0.2 },
      { monsterClass: 'Apple', probability: 0.3 },
      { monsterClass: 'Cucumber', probability: 0.35 },
    ];
    const rand = Math.random();
    let sum = 0;

    for (const monsterClass of monsterClasses) {
      sum += monsterClass.probability;
      if (rand < sum) {
        return monsterClass.monsterClass;
      }
    }

    return 'Cucumber';
  }

  private gameOver(): void {
    logging(
      LOG_EVENT.GAME_OVER,
      LOG_SCENE.DRAWING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.canvas.disable();
    this.ui.showTitle('Game Over');
    setTimeout(() => {
      this.ui.showGameOver(`1`, this.player.score.toString());
    }, 2000);
  }

  private gameClear(): void {
    logging(
      LOG_EVENT.GAME_CLEAR,
      LOG_SCENE.DRAWING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.canvas.disable();
    this.ui.showTitle('Clear!');
    this.player.trigger('CLEAR');

    this.isRunning = false;
    setTimeout(() => {
      const totalSeconds = Math.floor(this.duration / 1000);
      this.ui.showTitle(`${secondsFormatting(totalSeconds)}`);

      setTimeout(() => {
        sound.play('/audio/bonus.mp3', { loop: false, volume: Store.instance.state.volume as number });
        const score = secondsToScore(totalSeconds);
        this.player.score += score;
        this.ui.showTitle(`+${score} Score`);

        setTimeout(() => {
          if (!Store.instance.data.costumes[1]) {
            Store.instance.data.costumes[1] = true;
            Store.instance.save();
            this.ui.showGetItemPopup(1);
            this.ui.once('POPUP_END', () => {
              Store.instance.state.hp = this.player.hp;
              Store.instance.state.score = this.player.score;
              this.game.changeScene(SecondsCutScene);
            });
          } else {
            Store.instance.state.hp = this.player.hp;
            Store.instance.state.score = this.player.score;
            this.game.changeScene(SecondsCutScene);
          }
        }, 2500);
      }, 2500);
    }, 2500);
  }

  private generateMonsterQueue(count: number): void {
    for (let i = 0; i < count; i++) {
      this.monsterQueue.push(this.randomMonsterClass);
    }
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    this.ui.setHP(this.player.hp);
    this.ui.setScore(this.player.score);

    for (const object of this.objects) {
      object.update(elapsed);
    }

    this.ui.update(elapsed);
    this.canvas.update(elapsed);

    this.spawnDt += elapsed;

    if (this.spawnDt >= this.spawnMaxDt && this.monsterMaxLength > this.monsterLength) {
      this.spawnDt = 0;

      const monsterClassName = this.monsterQueue.pop();
      if (monsterClassName) {
        this.generateMonster(monsterClassName);
      }
    }
  }

  public destroy(): void {
    super.destroy();
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    const assets: Array<string> = [
      '/audio/bgm_stage_1.mp3',
      '/audio/getting_item.mp3',
      '/audio/heartget.mp3',
      '/audio/heartlose.mp3',
      '/audio/knife.mp3',
      '/audio/vegetable_die.mp3',

      '/textures/bg/bg_drawing.png',
      '/textures/ui/mouse.png',
      '/textures/objects/ninimo/draw_1.webp',
      '/textures/objects/ninimo/draw_2.webp',
      '/textures/objects/monster/pumpkin.webp',
      '/textures/objects/monster/watermelon.webp',
      '/textures/objects/monster/apple_1.webp',
      '/textures/objects/monster/apple_2.webp',
      '/textures/objects/monster/eggplant.webp',
      '/textures/objects/monster/cucumber.webp',
      '/textures/pattern/0.png',
      '/textures/pattern/1.png',
      '/textures/pattern/2.png',
      '/textures/pattern/3.png',
      '/textures/pattern/4.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  protected pointerdown = (event: InteractionEvent) => {
    if (this.game.paused) return;
    this.canvas.pointerdown(event);
  };

  protected pointermove = (event: InteractionEvent) => {
    if (this.game.paused) return;
    this.canvas.pointermove(event);
  };

  protected pointerup = (event: InteractionEvent) => {
    if (this.game.paused) return;
    this.canvas.pointerup(event);
  };

  public onBegin(): void {
    if (!this.isBgmPlaying) {
      sound.play('/audio/bgm_stage_1.mp3', { loop: true, volume: Store.instance.state.volume as number });
      this.isBgmPlaying = true;
    }
  }
}

export default DrawingGameScene;
